import React, { useEffect, useRef, useState } from 'react'
import * as api from '../../services/api.js'
import Card from '../../components/Card.jsx'
import TipBox from '../../components/TipBox.jsx'
import AppSelect from '../../components/AppSelect.jsx'

const REL_TYPE_OPTIONS = [
  { value: 'allies',      label: 'Allies' },
  { value: 'enemies',     label: 'Enemies' },
  { value: 'lovers',      label: 'Lovers' },
  { value: 'family',      label: 'Family' },
  { value: 'rivals',      label: 'Rivals' },
  { value: 'complicated', label: 'Complicated' },
]

const REL_COLORS = {
  allies: '#238b45', enemies: '#c0392b', lovers: '#e91e8c',
  family: '#A0722A', rivals: '#c4963a', complicated: '#7B4EAB',
}

export default function RelationshipMap({ pid = 'w' }) {
  const [chars, setChars]     = useState([])
  const [rels, setRels]       = useState([])
  const [project, setProject] = useState({})
  const [charA, setCharA]     = useState('')

  useEffect(() => {
    api.getCharacters(pid).then(d => setChars(d || []))
    api.getRelationships(pid).then(d => setRels(d || []))
    api.getWritingProjectData(pid).then(d => setProject(d || {}))
  }, [pid])
  const [charB, setCharB]     = useState('')
  const [relType, setRelType] = useState('allies')
  const canvasRef             = useRef(null)

  useEffect(() => { drawMap() }, [chars, rels])

  function drawMap() {
    const canvas = canvasRef.current
    if (!canvas || chars.length < 2) return
    const W = canvas.offsetWidth || 600
    canvas.width = W; canvas.height = 320
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, W, 320)

    const cx = W / 2, cy = 160, r = Math.min(cx - 60, 120)
    const angles = chars.map((_, i) => (2 * Math.PI * i / chars.length) - Math.PI / 2)
    const pos = chars.map((c, i) => ({
      x: cx + r * Math.cos(angles[i]),
      y: cy + r * Math.sin(angles[i]),
      name: c.name, id: c.id,
    }))

    // Draw relationship lines
    rels.forEach(rel => {
      const a = pos.find(p => p.id == rel.a)
      const b = pos.find(p => p.id == rel.b)
      if (!a || !b) return
      ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y)
      ctx.strokeStyle = REL_COLORS[rel.type] || '#8FBD8F'
      ctx.lineWidth = 2.5; ctx.globalAlpha = 0.7; ctx.stroke(); ctx.globalAlpha = 1
      ctx.font = '500 11px DM Sans, sans-serif'
      ctx.fillStyle = REL_COLORS[rel.type] || '#8FBD8F'
      ctx.textAlign = 'center'
      ctx.fillText(rel.type, (a.x + b.x) / 2, (a.y + b.y) / 2 - 5)
    })

    // Draw character nodes
    pos.forEach(p => {
      ctx.beginPath(); ctx.arc(p.x, p.y, 22, 0, 2 * Math.PI)
      ctx.fillStyle = 'white'; ctx.fill()
      ctx.strokeStyle = 'var(--accent, #238b45)'; ctx.lineWidth = 2; ctx.stroke()
      ctx.font = 'bold 11px DM Sans, sans-serif'
      ctx.fillStyle = '#0D2B12'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
      ctx.fillText(p.name.split(' ')[0].slice(0, 8), p.x, p.y)
    })
  }

  function addRelationship() {
    if (!charA || !charB || charA === charB) return
    const next = [...rels.filter(r => !(r.a == charA && r.b == charB) && !(r.a == charB && r.b == charA)), { a: charA, b: charB, type: relType }]
    setRels(next)
    api.saveRelationships(pid, next)
    setCharA(''); setCharB('')
  }

  const relsDescription = rels.map(r => {
    const a = chars.find(c => c.id == r.a)?.name || '?'
    const b = chars.find(c => c.id == r.b)?.name || '?'
    return `${a} ↔ ${b}: ${r.type}`
  }).join(', ')

  return (
    <Card label="Section 6" title="Character relationships">
      <TipBox title="💡 Mapping connections">
        <p>Relationships are the engine of fiction. How characters feel about each other — and how that changes — creates most of your drama. Map the key connections and note what each person wants from the other. Often the most interesting relationships are the ones with a hidden imbalance.</p>
      </TipBox>

      {chars.length < 2 ? (
        <p style={{ fontSize: '0.85rem', color: 'var(--stone)', fontStyle: 'italic', margin: '1rem 0' }}>
          Add at least two characters above to start mapping relationships.
        </p>
      ) : (
        <div style={{ marginTop: '1rem' }}>
          {/* Controls */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '0.75rem', alignItems: 'center' }}>
            <div style={{ flex: 1, minWidth: 120 }}>
              <AppSelect
                value={charA}
                onChange={setCharA}
                options={[{ value: '', label: 'Character A...' }, ...chars.map(c => ({ value: String(c.id), label: c.name }))]}
                placeholder="Character A..."
              />
            </div>
            <div style={{ flex: 1, minWidth: 120 }}>
              <AppSelect
                value={relType}
                onChange={setRelType}
                options={REL_TYPE_OPTIONS}
              />
            </div>
            <div style={{ flex: 1, minWidth: 120 }}>
              <AppSelect
                value={charB}
                onChange={setCharB}
                options={[{ value: '', label: 'Character B...' }, ...chars.map(c => ({ value: String(c.id), label: c.name }))]}
                placeholder="Character B..."
              />
            </div>
            <button className="ghost-btn" onClick={addRelationship}>Add</button>
          </div>

          {/* Canvas */}
          <canvas ref={canvasRef} style={{ width: '100%', borderRadius: 12, background: 'var(--surf)', display: 'block' }} height={320} />

          {/* Legend */}
          {rels.length > 0 && (
            <p style={{ fontSize: '0.75rem', color: 'var(--stone)', marginTop: '0.5rem' }}>
              {relsDescription}
            </p>
          )}
          {rels.length === 0 && (
            <p style={{ fontSize: '0.75rem', color: 'var(--stone)', marginTop: '0.5rem', fontStyle: 'italic' }}>
              No relationships added yet.
            </p>
          )}
        </div>
      )}

      <TipBox title="Mapping relationships">
        <ul>
          <li>What does each character want from the others — and do the others know this?</li>
          <li>Which relationship has the largest power imbalance? Power imbalances create drama.</li>
          <li>Which two characters would never choose to spend time together — and what would force them to?</li>
          <li>How does each relationship change from the beginning to the end of the story?</li>
        </ul>
      </TipBox>
    </Card>
  )
}
